import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAddressByPlaceId } from '../addressApis';
import { buildPostcodeAddressComponents } from '../utils/address';
import { config } from '@/config/config';

interface AddressOption {
  label: string;
  value: string;
  description?: string;
  placeId?: string;
  isPostcodeData?: boolean;
  postcodeData?: any;
  structured_formatting?: any;
}

interface AddressDetails {
  description?: string;
  label: string;
  placeId: string;
  location: {
    lat: number;
    lng: number;
  };
  gmaps: any;
}

interface UseGetFullAddressDetailsProps {
  address?: string;
}

const fetchAddressDetails = async (selected: AddressOption): Promise<AddressDetails> => {
  let addressDetails: AddressDetails;
  let addressComponents;
  let location;

  if (selected.isPostcodeData && selected.postcodeData) {
    const { postcodeData } = selected;
    location = {
      lat: postcodeData.latitude,
      lng: postcodeData.longitude,
    };

    addressComponents = buildPostcodeAddressComponents(postcodeData, selected.label);

    addressDetails = {
      description: selected.description,
      label: selected.label,
      placeId: `postcode_${postcodeData.postcode}`,
      location,
      gmaps: addressComponents,
    };
  } else {
    const result = await getAddressByPlaceId({
      placeId: selected.placeId!,
    });

    addressComponents = result.addressComponents;
    location = result.location;

    let addressLabel = selected.label;

    if (config.store.countryIdentifier === 'GB') {
      const postcodeComponent = addressComponents?.address_components?.find(
        (component: any) => component.types.includes('postal_code')
      );

      if (postcodeComponent) {
        const postcode = postcodeComponent.long_name;
        if (!addressLabel.includes(postcode)) {
          addressLabel = `${addressLabel}, ${postcode}`;
        }
      }
    }

    addressDetails = {
      description: selected.description,
      label: addressLabel,
      placeId: selected.placeId!,
      location,
      gmaps: addressComponents,
    };
  }

  return addressDetails;
};

export const useGetFullAddressDetails = ({
  address = '',
}: UseGetFullAddressDetailsProps = {}) => {
  const [selectedValue, setSelectedValue] = useState<AddressOption | null>(null);

  useEffect(() => {
    if (address) {
      setSelectedValue({ label: address, value: address });
    }
  }, [address]);

  const { data: fullAddressDetails, isLoading: isLoadingAddress, error, refetch } = useQuery({
    queryKey: ['addressDetails', selectedValue?.placeId],
    queryFn: () => fetchAddressDetails(selectedValue!),
    enabled: false,
    staleTime: 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const handleSelect = async (selected: AddressOption) => {
    setSelectedValue(selected);

    if (!selected?.placeId) {
      return;
    }

    const result = await refetch();
    return result.data;
  };

  return {
    handleSelect,
    selectedValue,
    isLoadingAddress,
    fullAddressDetails,
    error,
  };
};
